import { DataType, FormatPreset, IndexType } from '@doss/prisma/types'
import _ from 'lodash'
import { toDateTimeString } from './dates.js'
import { LocaleCode } from './locales.js'
import { parseNumberByLocale, parseNumberWithUnknownLocale } from './numbers.js'
import { isBooleanString } from './string.js'
import { isFormulaColumnType } from './table-column.js'
import {
  ArrayCellData,
  BooleanCellData,
  DateCellData,
  DateTimeCellData,
  DossDataFormat,
  EnumCellData,
  FileCellData,
  IdCellData,
  LookupCellData,
  NumberCellData,
  StringCellData,
  TableCellData,
  UserCellData,
  getEmptyArrayCell,
  getEmptyBooleanCell,
  getEmptyFileCell,
  getEmptyFormulaCell,
  getEmptyFormulaNumberCell,
  getEmptyLookupCell,
  getEmptyUserCell,
  getNumberIndexValue,
  stripIndexAffixes,
} from './types/table-cell.js'
import { isValidArrayInnerType } from './types/table-column.js'

export const DELIMITERS = [',', ';', '\t', '|'] as const

export type Delimiter = (typeof DELIMITERS)[number]

export const RECORD_ID_COLUMN_NAME = 'RecordID'
export const PARENT_ROW_ID_COLUMN_NAME = 'Parent Row ID'
export const BUTTON_NODES_COLUMN_NAME = 'Button Nodes'

export const DELIMITER_OPTIONS_MAP: Record<Delimiter, string> = {
  ',': 'Comma (,)',
  ';': 'Semicolon (;)',
  '\t': 'Tab',
  '|': 'Pipe (|)',
}

/**
 * Joins ARRAY / LOOKUP-array cell elements into a CSV cell, sorted by `localeCompare`.
 *
 * Why: ES keeps array elements in indexed-insertion order while DuckDB's `list()` aggregate
 * has no inherent ordering on a correlated subquery. Sorting in one shared place keeps the
 * server, worker, and client CSV paths byte-identical so COMPARE-mode diffs don't flag
 * order-only differences as cell mismatches.
 */
export function joinSortedArrayElements(values: unknown[]): string {
  return values
    .filter((v) => v !== null && v !== undefined)
    .map((v) => String(v))
    .sort((a, b) => a.localeCompare(b))
    .join(', ')
}

/** Data type of the minimum fields needed to get columns for csv upload */
type MinimizedColumn = {
  order: number
  indexAutogenerate: boolean
  type: DataType
}

export type TableColumnForTableExport = {
  id: string
  name: string
  order: number
  type: DataType
  innerType: DataType | null
  indexType: IndexType
  indexPrefix?: string
  indexSuffix?: string
  resolvedSourceType: DataType
  formatPreset?: FormatPreset | null
  lookupColumn: Omit<
    TableColumnForTableExport,
    'resolvedSourceType' | 'lookupColumn' | 'order'
  > | null
}

export type CellInput = {
  data?: TableCellData | null
  lookupCellIds?: string[] | null
  lookupCellValues?: string[] | null
  documentIds?: string[] | null
  userIds?: string[] | null
  column: CsvTableColumn
}

/**
 * Gets a list of all columns that are supported for CSV Upload.
 * For CREATE we exclude the following columns:
 * - System-generated index columns
 * - System generated formula columns
 * For UPDATE we exclude the following columns:
 * - System generated formula columns
 */
export function getColumnsForCsvUpload<T extends MinimizedColumn>(
  columns: T[],
  operation: 'Create' | 'Update',
): T[] {
  return _.orderBy(columns, (column) => column.order, ['asc'])
    .filter(
      (column) =>
        // Only filter out indexAutogenerate columns for CREATE operation
        operation === 'Update' || !column.indexAutogenerate,
    )
    .filter((column) => !isFormulaColumnType(column.type))
}

export type CsvTableColumn = {
  id: string
  name: string
  enumOptions: string[] | null
  isNullable: boolean
  type: DataType
  innerType: DataType | null
  indexPrefix: string
  indexSuffix: string
  indexType: IndexType
}

export interface CsvConversionContext {
  dateFormat?: string
  dateLocale?: LocaleCode
  numberLocale?: LocaleCode
  isBackend?: boolean
  timeZone: string
}

/**
 * Given a CSV cell value and the column it should match, returns a Cell Input for it
 */
export function convertCsvInputToCell(
  value: string,
  header: CsvTableColumn,
  context?: CsvConversionContext,
): CellInput {
  // Boolean columns are treated specially: they can be omitted from CSV uploads
  // regardless of nullable status, and will default to false. This provides a
  // better user experience as false is a meaningful default for boolean fields.
  if (value === '' && header.type === DataType.BOOLEAN) {
    return { column: header, data: getEmptyBooleanCell() }
  }

  // Throw an error if the column is not nullable and no value was provided
  if (value === '' && !header.isNullable) {
    throw new Error('Value is required')
  }
  // If the column accepts nullable values, and the input is empty, return an empty cell input
  if (value === '' && header.isNullable) {
    if (header.type === DataType.LOOKUP) {
      return {
        column: header,
        lookupCellValues: [],
        lookupCellIds: null,
        data: getEmptyLookupCell(),
      }
    } else if (header.type === DataType.ARRAY) {
      return { column: header, data: getEmptyArrayCell() }
    } else if (header.type === DataType.FORMULA) {
      return { column: header, data: getEmptyFormulaCell() }
    } else if (header.type === DataType.FORMULA_NUMBER) {
      return { column: header, data: getEmptyFormulaNumberCell() }
    } else if (header.type === DataType.FILE_URL) {
      return { column: header, documentIds: null, data: getEmptyFileCell() }
    } else if (header.type === DataType.USER) {
      return { column: header, userIds: null, data: getEmptyUserCell() }
    }
    return { column: header, data: null }
  }

  switch (header.type) {
    case DataType.ARRAY: {
      const arrayValues = value.split('|').map((element) => element.trim())

      const innerType = header.innerType
      if (!isValidArrayInnerType(innerType)) {
        return {
          column: header,
          data: { type: DossDataFormat.ARRAY, data: arrayValues },
        }
      }

      switch (innerType) {
        case DataType.LOOKUP: {
          // For lookup cells, return the lookup cell values rather than data
          const data: ArrayCellData = { type: DossDataFormat.ARRAY, data: [] }
          return {
            column: header,
            lookupCellValues: arrayValues,
            lookupCellIds: null,
            data,
          }
        }
        case DataType.FILE_URL: {
          // For file cells, return the document ids rather than data
          const data: ArrayCellData = { type: DossDataFormat.ARRAY, data: [] }
          return {
            column: header,
            data,
            documentIds: arrayValues,
          }
        }
        case DataType.USER: {
          // For file cells, return the document ids rather than data
          const data: ArrayCellData = { type: DossDataFormat.ARRAY, data: [] }
          return {
            column: header,
            data,
            userIds: arrayValues,
          }
        }
        case DataType.ENUM: {
          // Check that the values provided are enum options for the column
          const enumOptions = header.enumOptions
          if (!enumOptions) {
            throw new Error(`Column ${header.name} has no options`)
          }
          const invalidEnumOptions = arrayValues.filter((value) => !enumOptions.includes(value))
          if (invalidEnumOptions.length > 0) {
            throw new Error(
              `Invalid enum ${invalidEnumOptions.length === 1 ? 'value' : 'values'} ${invalidEnumOptions.map((option) => `"${option}"`).join(', ')} for column ${header.name}. Not in ${JSON.stringify(
                header.enumOptions,
              )}`,
            )
          }
          return {
            column: header,
            data: { type: DossDataFormat.ARRAY, data: arrayValues },
          }
        }
      }
      break
    }
    case DataType.FORMULA: {
      throw new Error('Formula type columns are computed so cannot have a value')
    }
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME: {
      throw new Error('Formula type columns are computed so cannot have a value')
    }
    case DataType.BOOLEAN: {
      if (!isBooleanString(value)) {
        throw new Error(`Invalid boolean value "${value}" for column ${header.name}`)
      }
      const data: BooleanCellData = {
        type: DossDataFormat.BOOLEAN,
        data: value.toLowerCase() === 'true',
      }
      return { column: header, data }
    }
    case DataType.DATE: {
      const data: DateCellData = {
        type: DossDataFormat.DATE,
        // Note: Values from CSV uploads are often plain date strings rather than ISO strings
        data: toDateTimeString(value, {
          setToMidnightUTC: true,
          customFormat: context?.dateFormat, // Primary source for matching date format
          locale: context?.dateLocale, // Secondary source for matching date format
          isDateString: true,
        }),
      }
      return { column: header, data }
    }
    case DataType.DATE_TIME: {
      const data: DateTimeCellData = {
        type: DossDataFormat.DATE_TIME,
        data: toDateTimeString(value, { setToMidnightUTC: false, timeZone: context?.timeZone }),
      }
      return { column: header, data }
    }
    case DataType.ENUM: {
      if (!header.enumOptions) {
        throw new Error(`Column ${header.name} has no options`)
      } else if (!header.enumOptions.includes(value)) {
        throw new Error(
          `Invalid enum value "${value}" for column ${header.name}. Not in ${JSON.stringify(
            header.enumOptions,
          )}`,
        )
      }
      const data: EnumCellData = { type: DossDataFormat.ENUM, data: value }
      return { column: header, data }
    }
    case DataType.FILE_URL: {
      const data: FileCellData = { type: DossDataFormat.FILE }
      return { column: header, data, documentIds: [value] }
    }
    case DataType.USER: {
      const data: UserCellData = { type: DossDataFormat.USER }
      return { column: header, data, userIds: [value] }
    }
    case DataType.ID: {
      const stripped = stripIndexAffixes(value, header.indexPrefix, header.indexSuffix)
      let data: IdCellData
      switch (header.indexType) {
        case IndexType.NONE:
          throw new Error('ID column cannot have index type NONE')
        case IndexType.NUMBER: {
          const index = getNumberIndexValue(stripped)
          if (_.isUndefined(index)) {
            throw new Error(`Invalid numeric index value "${value}" for column ${header.name}`)
          }
          data = { type: DossDataFormat.ID, data: index }
          break
        }
        case IndexType.STRING:
          data = { type: DossDataFormat.ID, data: stripped }
          break
      }
      return { column: header, data }
    }
    case DataType.LOOKUP: {
      const data: LookupCellData = { type: DossDataFormat.LOOKUP }
      return { column: header, data, lookupCellValues: [value], lookupCellIds: null }
    }
    case DataType.NUMBER: {
      let numberValue: number
      if (context?.numberLocale) {
        numberValue = parseNumberByLocale(value, context.numberLocale)
      } else {
        numberValue = parseNumberWithUnknownLocale(value)
      }
      if (Number.isNaN(numberValue)) {
        throw new Error(`Invalid numeric value "${value}" for column ${header.name}`)
      }
      const data: NumberCellData = { type: DossDataFormat.NUMBER, data: numberValue }
      return { column: header, data }
    }
    case DataType.STRING: {
      const data: StringCellData = { type: DossDataFormat.STRING, data: value }
      return { column: header, data }
    }
  }
}
